import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, CheckCircle2 } from "lucide-react";
import { api } from "../lib/api";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { trackFormSubmit } from "../lib/tracking";

const SERVICES = [
  "Supported Independent Living (SIL)",
  "Specialist Disability Accommodation (SDA)",
  "Short Term Accommodation (STA)",
  "Medium Term Accommodation (MTA)",
  "Community Participation",
  "Personal Care",
  "Daily Living Support",
];
const STATES = ["VIC", "WA"];

export default function ParticipantFormPage() {
  const [form, setForm] = useState({
    submitter_type: "participant",
    full_name: "",
    email: "",
    phone: "",
    participant_name: "",
    date_of_birth: "",
    state: "",
    suburb: "",
    ndis_number: "",
    services_needed: [],
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const toggle = (s) => setForm((f) => ({
    ...f,
    services_needed: f.services_needed.includes(s) ? f.services_needed.filter((x) => x !== s) : [...f.services_needed, s],
  }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email || !form.phone) {
      toast.error("Please complete name, email and phone.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/referrals/participant", form);
      trackFormSubmit("participant_referral", { state: form.state, services: form.services_needed.length });
      setDone(true);
      toast.success("Thank you. We'll be in touch within 1 business day.");
    } catch (e) {
      toast.error("Could not submit. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) return (
    <div className="alpha-container py-20 max-w-xl text-center" data-testid="participant-form-success">
      <CheckCircle2 className="w-14 h-14 text-[color:var(--alpha-purple-700)] mx-auto" />
      <h1 className="mt-4 text-3xl font-display font-semibold">Thanks — we've got it.</h1>
      <p className="mt-3 text-[color:var(--alpha-mute)]">A member of the Alpha team will be in touch shortly. If it's urgent, call us on <strong>1300 380 692</strong>.</p>
      <Link to="/" className="alpha-btn-ghost mt-6 inline-flex text-sm">Back to home</Link>
    </div>
  );

  return (
    <div data-testid="page-participant-form">
      <section className="alpha-gradient-hero border-b border-[color:var(--alpha-line)]">
        <div className="alpha-container pt-10 pb-8">
          <Link to="/referrals" className="inline-flex items-center gap-2 text-sm text-[color:var(--alpha-mute)] hover:text-[color:var(--alpha-purple-700)]">
            <ArrowLeft className="w-4 h-4" /> Back
          </Link>
          <div className="alpha-overline mt-4">Participant / family referral</div>
          <h1 className="mt-3 text-3xl sm:text-4xl font-display font-semibold tracking-tight">Tell us a little about you.</h1>
          <p className="mt-3 text-[color:var(--alpha-mute)] max-w-2xl">Plain-English, mobile-friendly, no NDIS jargon required. You don't need to have everything sorted — we'll help you from here.</p>
        </div>
      </section>

      <section className="alpha-container py-12 max-w-3xl">
        <form onSubmit={submit} className="alpha-card p-6 sm:p-10 space-y-6" data-testid="participant-form">
          <div>
            <label className="alpha-overline">I am a</label>
            <Select value={form.submitter_type} onValueChange={(v) => setForm({ ...form, submitter_type: v })}>
              <SelectTrigger className="mt-2 h-11" data-testid="p-form-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="participant">Participant</SelectItem>
                <SelectItem value="family">Family member / carer</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-semibold">Your full name *</label>
              <Input className="mt-2 h-11" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} required data-testid="p-form-name" />
            </div>
            <div>
              <label className="text-sm font-semibold">Email *</label>
              <Input type="email" className="mt-2 h-11" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required data-testid="p-form-email" />
            </div>
            <div>
              <label className="text-sm font-semibold">Phone *</label>
              <Input className="mt-2 h-11" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} required data-testid="p-form-phone" />
            </div>
            <div>
              <label className="text-sm font-semibold">Participant name (if different)</label>
              <Input className="mt-2 h-11" value={form.participant_name} onChange={(e) => setForm({ ...form, participant_name: e.target.value })} data-testid="p-form-pname" />
            </div>
            <div>
              <label className="text-sm font-semibold">State</label>
              <Select value={form.state || "any"} onValueChange={(v) => setForm({ ...form, state: v === "any" ? "" : v })}>
                <SelectTrigger className="mt-2 h-11" data-testid="p-form-state"><SelectValue placeholder="Select state" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="any">— Select —</SelectItem>
                  {STATES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-semibold">Suburb</label>
              <Input className="mt-2 h-11" value={form.suburb} onChange={(e) => setForm({ ...form, suburb: e.target.value })} data-testid="p-form-suburb" />
            </div>
            <div>
              <label className="text-sm font-semibold">NDIS number (optional)</label>
              <Input className="mt-2 h-11" value={form.ndis_number} onChange={(e) => setForm({ ...form, ndis_number: e.target.value })} data-testid="p-form-ndis" />
            </div>
            <div>
              <label className="text-sm font-semibold">Participant DOB (optional)</label>
              <Input type="date" className="mt-2 h-11" value={form.date_of_birth} onChange={(e) => setForm({ ...form, date_of_birth: e.target.value })} data-testid="p-form-dob" />
            </div>
          </div>

          <div>
            <label className="text-sm font-semibold">What supports are you looking for?</label>
            <div className="mt-3 grid sm:grid-cols-2 gap-2">
              {SERVICES.map((s) => (
                <label key={s} className="flex items-center gap-3 p-3 rounded-xl border border-[color:var(--alpha-line)] hover:bg-[color:var(--alpha-soft)] cursor-pointer">
                  <Checkbox checked={form.services_needed.includes(s)} onCheckedChange={() => toggle(s)} data-testid={`p-form-svc-${s.toLowerCase().slice(0, 8).replace(/\s+/g, "-")}`} />
                  <span className="text-sm">{s}</span>
                </label>
              ))}
            </div>
          </div>

          <div>
            <label className="text-sm font-semibold">Anything else we should know?</label>
            <Textarea className="mt-2" rows={4} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="Goals, preferences, support needs…" data-testid="p-form-message" />
          </div>

          <button type="submit" disabled={submitting} className="alpha-btn-primary" data-testid="p-form-submit">
            {submitting ? "Sending…" : <>Submit referral <ArrowRight className="w-4 h-4" /></>}
          </button>
        </form>
      </section>
    </div>
  );
}
